import { useMemo, useRef, useState, type ReactNode } from "react";
import type { ListControllerResult } from "ra-core";
import type { RaRecord } from "react-admin";
import {
  Datagrid,
  FunctionField,
  ReferenceManyField,
  TextField,
  NumberField,
  useShowContext,
  Button,
  useUnselectAll,
  useListContext,
  ListContextProvider,
  useNotify,
  useRefresh,
} from "react-admin";
import {
  Paper,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CancelIcon from "@mui/icons-material/Cancel";
import UpdateIcon from "@mui/icons-material/Update";
import WriteoffDialog from "@/containers/sabil/sabil-ledger/WriteoffDialog";
import BalanceSummary, { type BalanceSummaryHandle } from "./BalanceSummary";
import { formatDisplayDateTime } from "@/utils/date-format";
import { formatLedgerPeriodLabel } from "../sabil-show-display";
import { callApi } from "@/dataprovider/misc-apis";

type LedgerRow = RaRecord & {
  month?: number;
  year?: number;
  status?: string;
  dueAmount?: number;
  paidAmount?: number;
  writeoffAmount?: number;
};

const OPENING_BALANCE_TOOLTIP =
  "Financial-year opening balance snapshot. Outstanding is still due minus paid for each row.";

const OpeningBalanceLabel = () => (
  <Tooltip title={OPENING_BALANCE_TOOLTIP}>
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      Opening Balance
      <InfoOutlinedIcon sx={{ fontSize: "1rem", color: "text.secondary" }} />
    </Box>
  </Tooltip>
);

type UpdateTakhmeenDialogProps = {
  open: boolean;
  onClose: () => void;
  selectedIds: (string | number)[];
  sabilId?: string | number;
  onSuccess?: () => void;
};

const UpdateTakhmeenDialog = ({
  open,
  onClose,
  selectedIds,
  sabilId,
  onSuccess,
}: UpdateTakhmeenDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const notify = useNotify();
  const refresh = useRefresh();

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!selectedIds || selectedIds.length === 0 || !sabilId) {
      setError("Please select at least one ledger entry");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await callApi({
        location: "sabilLedger",
        method: "POST",
        id: "update-takhmeen",
        data: {
          sabilId,
          ledgerEntryIds: selectedIds,
        },
      });

      if (response?.data) {
        const payload = response.data as { count?: number };
        notify(`Successfully updated ${payload.count || selectedIds.length} ledger entry/entries`, {
          type: "success",
        });
        refresh();
        onSuccess?.();
        handleClose();
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = e?.response?.data?.message || e?.message || "Failed to update takhmeen";
      setError(errorMessage);
      notify(errorMessage, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Takhmeen</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to update the due amount for {selectedIds?.length || 0} ledger
            entry/entries to the current takhmeen?
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
          label="Update Takhmeen"
        />
      </DialogActions>
    </Dialog>
  );
};

type LedgerBulkActionButtonsProps = {
  onWriteoffSuccess: () => void;
  sabilId?: string | number;
};

const LedgerBulkActionButtons = ({ onWriteoffSuccess, sabilId }: LedgerBulkActionButtonsProps) => {
  const { selectedIds, resource } = useListContext();
  const [writeoffOpen, setWriteoffOpen] = useState(false);
  const [updateTakhmeenOpen, setUpdateTakhmeenOpen] = useState(false);
  const unselectAll = useUnselectAll(resource);

  const handleWriteoffClose = () => {
    setWriteoffOpen(false);
    unselectAll();
    onWriteoffSuccess();
  };

  const handleUpdateTakhmeenClose = () => {
    setUpdateTakhmeenOpen(false);
    unselectAll();
    onWriteoffSuccess();
  };

  if (!selectedIds || selectedIds.length === 0) {
    return <div style={{ marginLeft: "25px" }} />;
  }

  return (
    <>
      <Button
        label="Update Takhmeen"
        onClick={() => setUpdateTakhmeenOpen(true)}
        startIcon={<UpdateIcon />}
        sx={{ ml: 1 }}
      >
        Update Takhmeen ({selectedIds.length})
      </Button>
      <Button
        label="Write Off"
        onClick={() => setWriteoffOpen(true)}
        startIcon={<CancelIcon />}
        sx={{ ml: 1 }}
      >
        Write Off ({selectedIds.length})
      </Button>
      <UpdateTakhmeenDialog
        open={updateTakhmeenOpen}
        onClose={handleUpdateTakhmeenClose}
        selectedIds={selectedIds}
        sabilId={sabilId}
        onSuccess={handleUpdateTakhmeenClose}
      />
      <WriteoffDialog
        open={writeoffOpen}
        onClose={handleWriteoffClose}
        selectedIds={selectedIds}
        resource={resource}
      />
    </>
  );
};

const StatusChip = ({ status }: { status?: string }) => {
  const statusConfig: Record<
    string,
    { label: string; color: "error" | "warning" | "success" | "default" }
  > = {
    UNPAID: { label: "Unpaid", color: "error" },
    PARTIALLY_PAID: { label: "Partially Paid", color: "warning" },
    FULLY_PAID: { label: "Fully Paid", color: "success" },
    WRITTEN_OFF: { label: "Written Off", color: "default" },
  };

  const config = (status && statusConfig[status]) || {
    label: status || "",
    color: "default" as const,
  };

  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      sx={{
        fontWeight: 600,
        minWidth: 100,
      }}
    />
  );
};

const LedgerPeriodField = ({ record, sabilType }: { record: LedgerRow; sabilType?: string }) => (
  <Typography variant="body2" sx={{ fontWeight: 500 }}>
    {formatLedgerPeriodLabel(record.month, record.year, sabilType)}
  </Typography>
);

const LedgerSortBridge = ({ children }: { children: ReactNode }) => {
  const listContext = useListContext<LedgerRow>();

  const value = useMemo((): ListControllerResult<LedgerRow> => {
    if (listContext.isPending || listContext.data == null) {
      return listContext;
    }

    const sortedData = [...listContext.data].sort((a, b) => {
      const yearDiff = (b.year ?? 0) - (a.year ?? 0);
      if (yearDiff !== 0) {
        return yearDiff;
      }
      return (b.month ?? 0) - (a.month ?? 0);
    });

    return {
      ...listContext,
      data: sortedData,
    } as ListControllerResult<LedgerRow>;
  }, [listContext]);

  return <ListContextProvider value={value}>{children}</ListContextProvider>;
};

type LedgerDatagridProps = {
  sabilId?: string | number;
  sabilType?: string;
  onWriteoffSuccess: () => void;
};

const LedgerDatagrid = ({ sabilId, sabilType, onWriteoffSuccess }: LedgerDatagridProps) => (
  <LedgerSortBridge>
    <Datagrid
      rowClick={false}
      bulkActionButtons={
        <LedgerBulkActionButtons onWriteoffSuccess={onWriteoffSuccess} sabilId={sabilId} />
      }
      isRowSelectable={(ledgerEntry) => ledgerEntry.status !== "WRITTEN_OFF"}
      sx={(theme) => ({
        "& .RaDatagrid-headerCell": {
          fontWeight: 600,
          bgcolor: alpha(theme.palette.text.primary, 0.06),
        },
      })}
    >
      <FunctionField
        label="Period"
        render={(entry: LedgerRow) => <LedgerPeriodField record={entry} sabilType={sabilType} />}
      />
      <TextField source="financialYear" label="Financial Year" />
      <NumberField
        source="dueAmount"
        label="Due Amount"
        options={{ style: "currency", currency: "INR", minimumFractionDigits: 0 }}
        sx={{ fontWeight: 600 }}
      />
      <NumberField
        source="paidAmount"
        label="Paid Amount"
        options={{ style: "currency", currency: "INR", minimumFractionDigits: 0 }}
        sx={{
          color: "success.main",
          fontWeight: 500,
        }}
      />
      <NumberField
        source="openingBalance"
        label={<OpeningBalanceLabel />}
        options={{ style: "currency", currency: "INR", minimumFractionDigits: 0 }}
      />
      <FunctionField label="Status" render={(entry) => <StatusChip status={entry.status} />} />
      <FunctionField
        label="Outstanding"
        render={(ledgerEntry) => {
          if (ledgerEntry.status === "WRITTEN_OFF") {
            return (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontStyle: "italic",
                }}
              >
                Written Off: ₹{(ledgerEntry.writeoffAmount || 0).toLocaleString("en-IN")}
              </Typography>
            );
          }
          const outstanding = (ledgerEntry.dueAmount || 0) - (ledgerEntry.paidAmount || 0);
          return (
            <Typography
              variant="body2"
              sx={{
                fontWeight: outstanding > 0 ? 600 : 400,
                color: outstanding > 0 ? "error.main" : "text.secondary",
              }}
            >
              ₹{Math.max(0, outstanding).toLocaleString("en-IN")}
            </Typography>
          );
        }}
      />
      <FunctionField
        label="Created"
        sortBy="createdAt"
        sx={{ fontSize: "0.875rem" }}
        render={(record: LedgerRow) => formatDisplayDateTime(record?.createdAt, { empty: "—" })}
      />
    </Datagrid>
  </LedgerSortBridge>
);

const Ledger = () => {
  const { record } = useShowContext<RaRecord>();
  const balanceSummaryRef = useRef<BalanceSummaryHandle | null>(null);
  const sabilType = record?.sabilType as string | undefined;

  const handleWriteoffSuccess = () => {
    balanceSummaryRef.current?.refresh();
  };

  return (
    <Box sx={{ p: 1 }}>
      <BalanceSummary ref={balanceSummaryRef} />

      <Paper elevation={1} sx={{ p: 1 }}>
        <ReferenceManyField
          reference="sabilLedger"
          target="sabilId"
          label={false}
          filter={{ sabilId: record?.id }}
          sort={{ field: "year", order: "DESC" }}
        >
          <LedgerDatagrid
            sabilId={record?.id}
            sabilType={sabilType}
            onWriteoffSuccess={handleWriteoffSuccess}
          />
        </ReferenceManyField>
      </Paper>
    </Box>
  );
};

export default Ledger;
